import { getTranslation } from "./i18n";
import SettingsPersistor from "./settings-persistor";
import setupDevTools from "./dev-tools";
import config from "./config";
import fontSizeModule from '../modules/font-size';
import letterSpacingModule from '../modules/letter-spacing';
import lineHeightModule from '../modules/line-height';
import wordSpacingModule from '../modules/word-spacing';
import contrastModesModule from '../modules/contrast-modes';
import enlargedButtonsModule from '../modules/enlarged-buttons';
import stopAnimationsModule from '../modules/stop-animations';
import readingAidModule from '../modules/reading-aid';
import simpleLangToggleModule from '../modules/simple-lang-toggle';
import simpleLangButtonModule from '../modules/simple-lang-button';
import cursorSizeModule from '../modules/cursor-size';

export default () => {
  document.addEventListener("DOMContentLoaded", () => {
    const a11ySettings: Record<string, any> = SettingsPersistor.load();

    const availableModules = [
      'font-size', 
      'word-spacing', 
      'letter-spacing', 
      'line-height', 
      'cursor-size', 
      'contrast-modes', 
      'enlarged-buttons', 
      'simple-lang-toggle', 
      'simple-lang-button', 
      'reading-aid', 
      'magnifier', 
      'stop-animations'
    ];

    const modulesWithOutStyles = [
      'simple-lang-toggle',
      'simple-lang-button'
    ];
    const a11yModules: string[] = config.modules.filter(module => availableModules.includes(module));

    const allModules = [
      fontSizeModule,
      wordSpacingModule,
      letterSpacingModule,
      lineHeightModule,
      cursorSizeModule,
      contrastModesModule,
      enlargedButtonsModule,
      simpleLangToggleModule,
      simpleLangButtonModule,
      readingAidModule,
      stopAnimationsModule,
    ];

    // < --------------- MENU INIT --------------- > //
    const menuContainer = document.createElement("div");
    menuContainer.innerHTML = `
      <button id="a11y-menu-toggle" class="a11y-menu-toggle" aria-haspopup="true" aria-label="${getTranslation('openA11ySettingsAria')}" aria-expanded="false" data-translation-aria-field="true" data-translation-key="openA11ySettingsAria">
        <span class="a11y-menu-toggle-icon" style="pointer-events: none;">
          <svg class="a11y-icon" aria-hidden="true" focusable="false" viewBox="0 0 51 51" xmlns="http://www.w3.org/2000/svg">
            <path d="M25.5 0C11.417 0 0 11.417 0 25.5S11.417 51 25.5 51 51 39.583 51 25.5 39.583 0 25.5 0zm-.385 5.064a3.3 3.3 0 0 1 3.307 3.291 3.304 3.304 0 0 1-3.307 3.306 3.3 3.3 0 0 1-3.29-3.306 3.29 3.29 0 0 1 3.29-3.291zm14.289 10.652l-9.809 1.24.005 9.817 4.755 15.867a1.85 1.85 0 0 1-1.344 2.252c-.989.25-2.003-.3-2.252-1.298l-4.87-14.443h-1.498l-4.48 14.742c-.374.964-1.448 1.404-2.407 1.03-.954-.37-1.533-1.454-1.158-2.418l4.115-15.572v-9.978l-9.04-1.228c-.928-.075-1.558-.89-1.483-1.818.07-.934.914-1.628 1.838-1.554l10.982.944h4.815l11.69-.963a1.68 1.68 0 0 1 1.749 1.623c.04.924-.68 1.718-1.608 1.758z" fill="currentColor"></path>
          </svg>
        </span>
        <span class="a11y-menu-toggle-text" data-translation-field="true" data-translation-key="a11y">${getTranslation('a11y')}</span>
      </button>
    `;
    menuContainer.className = "a11y-menu-container";
    menuContainer.id = 'a11y-widget';
    document.body.insertBefore(menuContainer, document.body.children[1]);

    const menuDrawer = document.createElement("div");
    menuDrawer.className = "a11y-menu-drawer";
    menuContainer.appendChild(menuDrawer);

    const menu = document.createElement("div");
    menu.inert = true;
    menu.className = "a11y-menu";
    menu.innerHTML = `
      <div class="a11y-headline"><span class="a11y-headline-text" data-translation-field="true" data-translation-key="settings">${getTranslation('settings')}</span><button class="a11y-close-menu" data-translation-aria-field="true" data-translation-key="closeA11ySettingsAria" aria-label="${getTranslation('closeA11ySettingsAria')}"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="m12 13.4l2.9 2.9q.275.275.7.275t.7-.275t.275-.7t-.275-.7L13.4 12l2.9-2.9q.275-.275.275-.7t-.275-.7t-.7-.275t-.7.275L12 10.6L9.1 7.7q-.275-.275-.7-.275t-.7.275t-.275.7t.275.7l2.9 2.9l-2.9 2.9q-.275.275-.275.7t.275.7t.7.275t.7-.275zm0 8.6q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-8"/></svg></button></div>
    `;
    const modulesContainer = document.createElement("div");
    modulesContainer.className = "a11y-modules-container";
    menu.appendChild(modulesContainer);
    menuDrawer.appendChild(menu);

    // < --------------- STYLE SETTINGS --------------- > //
    const style = document.createElement("style");
    let styleContents = ``;

    
    // < --------------- LOAD MODULES --------------- > //
    for (const moduleName of a11yModules) {
      const mod = allModules.find(m => m.name === moduleName);
      if (mod) {
        const moduleStyle = mod.setup(a11ySettings, modulesContainer);
        if (typeof moduleStyle === 'string' && moduleStyle.trim().length > 0) {
          styleContents += moduleStyle;
        }
      }
    }

    const resetAllElement = document.createElement("div");
    resetAllElement.className = "a11y-reset-all";
    resetAllElement.innerHTML = `
    <button id="a11y-reset-all" class="a11y-button a11y-reset-all-button" data-translation-field="true" data-translation-key="resetAll">${getTranslation('resetAll')}</button>
    `;
    modulesContainer.appendChild(resetAllElement);

    const resetAllButton = <HTMLButtonElement> document.getElementById("a11y-reset-all");
    resetAllButton.addEventListener("click", () => {
      SettingsPersistor.reset();
      for (const moduleName of a11yModules) {
        const mod = allModules.find(m => m.name === moduleName);
        if (mod) mod.reset();
      }
    });

    modulesContainer.lastChild?.addEventListener("focusout", (event) => {
      event.stopPropagation();
      const e = <FocusEvent> event;
      if (!modulesContainer.contains(e.relatedTarget as Node)) {
        menuToggle?.focus();
      }
    });

    // < --------------- MENU STYLING --------------- > //
    let a11yToggleBorderRadiuses = '0 0 0 0'; 
    let a11yPositionStyles = `
        top: 0;
        right: 10rem;
    `;
    let a11yAlignment = `
        flex-direction: column;
        align-items: flex-end;
    `;
    switch (config.widgetPos.desktop) {
      case 'top-left':
        a11yToggleBorderRadiuses = '0 0 var(--a11y-border-radius) var(--a11y-border-radius)';
        a11yPositionStyles = `
        top: 0;
        left: var(--a11y-offset-x);
        `;
        a11yAlignment = `
        flex-direction: column;
        align-items: flex-start;
        `;
        break;
      case 'top-right':
        a11yToggleBorderRadiuses = '0 0 var(--a11y-border-radius) var(--a11y-border-radius)';
        a11yPositionStyles = `
        top: 0;
        right: var(--a11y-offset-x);
        `;
        a11yAlignment = `
        flex-direction: column;
        align-items: flex-end;
        `;
        break;
      case 'bottom-left':
        a11yToggleBorderRadiuses = 'var(--a11y-border-radius) var(--a11y-border-radius) 0 0';
        a11yPositionStyles = `
        bottom: 0;
        left: var(--a11y-offset-x);
        `;
        a11yAlignment = `
        flex-direction: column-reverse;
        align-items: flex-start;
        `;
        break;
      case 'bottom-right':
        a11yToggleBorderRadiuses = 'var(--a11y-border-radius) var(--a11y-border-radius) 0 0';
        a11yPositionStyles = `
        bottom: 0;
        right: var(--a11y-offset-x);
        `;
        a11yAlignment = `
        flex-direction: column-reverse;
        align-items: flex-end;
        `;
        break;
    }

    let a11yMobilePosition = `top: var(--a11y-offset-y);
    right: 0;`;
    let a11yMobileAlignment = `
        flex-direction: column;
        align-items: flex-end;
    `;
    let a11yMobileToggleMargin = 'margin-right: 10px;';
    switch (config.widgetPos.mobile) {
      case 'top-left':
        a11yMobileAlignment = `
        flex-direction: column;
        align-items: flex-start;
        `;
        a11yMobilePosition = `top: var(--a11y-offset-y);
        left: 0px;`;
        a11yMobileToggleMargin = 'margin-left: var(--a11y-offset-x);';
        break;
      case 'top-right':
        a11yMobileAlignment = `
        flex-direction: column;
        align-items: flex-end;
        `;
        a11yMobilePosition = `top: var(--a11y-offset-y);
        right: 0;`;
        a11yMobileToggleMargin = 'margin-right: var(--a11y-offset-x);';
        break;
      case 'bottom-left':
        a11yMobileAlignment = `
        flex-direction: column-reverse;
        align-items: flex-start;
        `;
        a11yMobilePosition = `bottom: var(--a11y-offset-y);
        left: 0;`;
        a11yMobileToggleMargin = 'margin-left: var(--a11y-offset-x);';
        break;
      case 'bottom-right':
        a11yMobileAlignment = `
        flex-direction: column-reverse;
        align-items: flex-end;
        `;
        a11yMobilePosition = `bottom: var(--a11y-offset-y);
        right: 0;`;
        a11yMobileToggleMargin = 'margin-right: var(--a11y-offset-x);';
        break;
    }

    styleContents += `
    .a11y-menu-container {
      position: fixed;
      ${a11yPositionStyles}
      display: flex;
      ${a11yAlignment}
      gap: 24px;
      pointer-events: none;
      z-index: var(--a11y-z-index);

      *:focus:not(:focus-visible) { outline: none }
    }

    .a11y-menu-drawer {
      height: 100%;
      max-height: 0px;
      overflow: hidden;
      transition: max-height 350ms var(--a11y-cb);
    }

    .a11y-menu-drawer.open {
      max-height: var(--drawer_height);
    }

    .a11y-menu-toggle {
      display: flex;
      gap: 8px;
      align-items: center;
      padding: 12px 16px;
      border-radius: ${a11yToggleBorderRadiuses};
      border: 1px solid var(--a11y-color-widget-border);
      cursor: pointer;
      background-color: var(--a11y-color-widget-bg);
      color: var(--a11y-color-widget-text);
      pointer-events: all;
      transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.4s cubic-bezier(0.4,0,0.2,1);

      .a11y-icon {
        width: 24px;
        height: 24px;
      }
    }

    .a11y-menu-toggle--hidden-left {
      transform: translateX(-150%);
      opacity: 0;
      pointer-events: none;
    }
    .a11y-menu-toggle--hidden-right {
      transform: translateX(150%);
      opacity: 0;
      pointer-events: none;
    }

    .a11y-menu {
      display: flex;
      flex-direction: column;

      background-color: var(--a11y-color-widget-bg);
      color: var(--a11y-color-widget-text);
      border: 1px solid var(--a11y-color-widget-border);
      padding: 0.75rem;
      font-size: 16px;
      border-radius: var(--a11y-border-radius);
      pointer-events: all;

      .a11y-close-menu {
        display: none;
        background-color: transparent;
        border: none;
        color: var(--a11y-color-widget-text);
        padding: 0;
        margin: 0;
        line-height: 0.5;
      }
    }

    .a11y-headline {
      font-size: 2em;
      font-weight: 700;
      margin: 0px 0px 32px 0px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    }

    .a11y-modules-container {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .a11y-module {
      display: grid;
      grid-template-columns: auto 256px;
      align-items: center;
      gap: 24px;

      select {
        border-radius: 0px;
        border: 1px solid black;
        padding: 4px;
      }
    }

    .a11y-controls {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      width: 100%;
    }
    
    .a11y-controls button {
      cursor: pointer;
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--a11y-color-primary);
      height: 24px;
      width: 24px;
      font-size: 20px !important;
      border: 1px solid var(--a11y-color-primary);
      color: var(--a11y-color-secondary);
      letter-spacing: normal !important;
      padding: 0;
      line-height: 0;
      outline-offset: 2px;
      transition: color 350ms var(--a11y-cb), background-color 350ms var(--a11y-cb);

      &:hover {
        background-color: transparent;
        color: var(--a11y-color-primary);
      }

      &.disabled {
        cursor: not-allowed;
        border-color: black;
        background-color: transparent;
        color: black;
        opacity: .4;
      }

      &.a11y-reset {
        color: var(--a11y-color-primary);
        border: 0px solid transparent;
        background-color: transparent;
        font-size: 1.5em !important;
        transition: scale 350ms var(--a11y-cb);

        .a11y-reset-icon {
          transform: translateY(-2px);
        }

        &:hover {
          scale: 1.2;
        }
      }
    }

    .a11y-module-analyzer button {
      background-color: var(--a11y-color-primary);
      border: 1px solid var(--a11y-color-primary);
      color: var(--a11y-color-secondary);
      letter-spacing: normal !important;
      transition: color 350ms var(--a11y-cb), background-color 350ms var(--a11y-cb);

      &:hover {
        cursor: pointer;
        background-color: transparent;
        color: var(--a11y-color-primary);
      }
    }

    .a11y-range {
      width: 100%;
      flex: 1 1 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      input[type="range"] {
        flex: 1 1 auto;
        -webkit-appearance: none;
        background: transparent;
        border: 1px solid black;
        border-radius: 8px;
        height: 8px;
        width: 100%;
        padding: 0;
        outline-offset: 2px;
      }

      input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        cursor: pointer;
        background-color: var(--a11y-color-primary);
        border-radius: 12px;
        height: 20px;
        width: 20px;
      }

      input[type="range"]::-moz-range-thumb {
        cursor: pointer;
        background-color: var(--a11y-color-primary);
        border-radius: 12px;
        height: 21px;
        width: 21px;
      }

      .range-group__marker {
        background-color: var(--a11y-color-primary);
        height: 24px;
        width: 1px;
        position: absolute;
        top: 50%;
        left: var(--marker-pos);
        transform: translate(0, -50%);

        &--zero {
          transform: translate(12px, -50%);
        }
      }
    }

    .a11y-reset-all {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
    }

    .a11y-reset-all-button, .a11y-menu a {
      width: 100%;
      letter-spacing: normal !important;
      text-decoration: none;
      cursor: pointer;
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--a11y-color-primary);
      border: 1px solid var(--a11y-color-primary);
      color: var(--a11y-color-secondary);
      padding: 6px 24px;
      transition: color 350ms var(--a11y-cb), background-color 350ms var(--a11y-cb);

      &:hover {
        background-color: transparent;
        color: var(--a11y-color-primary);
      }
    }

    .a11y-toggle {
      position: relative;
      display: inline-block;
      width: 54px;
      height: 26px;

      .screen-reader-text {
        display: none;
      }
    }

    .a11y-toggle input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    .slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--a11y-color-secondary);
      -webkit-transition: .4s;
      transition: .4s;
      border-radius: 24px;
      border: 1px solid darkgray;
    }

    .slider:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 2px;
      bottom: 2px;
      background-color: var(--a11y-color-primary);
      -webkit-transition: .4s;
      transition: .4s;
      border-radius: 50%;
    }

    input:checked + .slider {
      background-color: var(--a11y-color-primary);
    }

    input:checked + .slider:before {
      background-color: var(--a11y-color-secondary);
    }

    input:focus + .slider {
      box-shadow: 0 0 1px var(--a11y-color-primary);
    }

    input:checked + .slider:before {
      -webkit-transform: translateX(26px);
      -ms-transform: translateX(26px);
      transform: translateX(26px);
    }

    @media screen and (max-width: 1200px) {
      .a11y-menu-container {
        max-width: 100vw;
        width: 100%;
        gap: 8px;
        ${a11yMobileAlignment}
        ${a11yMobilePosition}

        .a11y-menu-toggle {
          width: var(--a11y-menu-toggle-width-mobile);
          height: var(--a11y-menu-toggle-height-mobile);
          padding: calc((var(--a11y-menu-toggle-height-mobile) - 24px - 2px) / 2);
          border-radius: 50%;
          justify-content: center;
          align-items: center;
          box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12);
          ${a11yMobileToggleMargin}

          .a11y-icon {
            width: 100%;
            height: 100%;
          }

          .a11y-menu-toggle-text {
            display: none;
          }
        }

        .a11y-menu-drawer {
          padding: 0 12px;
          max-width: 100vw;
          width: 100%;
        }

        .a11y-headline {
          line-height: 1;
          margin: 0px 0px 6px 0px;
        }

        .a11y-close-menu {
          display: block;
        }

        .a11y-modules-container {
          gap: 12px;

          .a11y-module {
            grid-template-columns: repeat(2, 1fr);
          }

          .a11y-module:has(.a11y-range), .a11y-module-contrast-mode {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }

          .a11y-module-contrast-modes {
            select {
              width: 100%;
            }
          }

          .a11y-module-cursor-size {
            display: none !important;
          }
        }
      }
    }
    `;

    // .a11y-powered-by {
    //   font-size: 12px;
    //   align-self: flex-end;
    //   display: flex;
    //   gap: 6px;
    //   align-items: center;
    // }

    // const authorContainer = document.createElement("div");
    // authorContainer.className = "a11y-powered-by";
    // authorContainer.innerHTML = `<span>Powered bei zeit:raum</span> <svg viewBox="0 0 30 69" height="20" width="9" xmlns="http://www.w3.org/2000/svg"><path d="m30 17.6775197v-17.6775197h-15.2261985l-14.65901419 17.1812615-.11478731.1341239v17.6708135h13.9500338l-13.83524649 16.2021576-.11478731.1341238v17.6775197h15.2329507l14.6455098-17.1879677.1215395-.1274177v-17.6708135h-13.9432816l13.8217421-16.2021576zm-.9790682-.6706191h-13.4908845v-16.03450289h13.5043889zm-13.5043889 34.9727864h12.9642133l-12.9642133 15.2029352zm-13.97029037-.9656915 12.96421337-15.1895228v15.1895228zm12.96421337-34.0003887h-12.96421337l12.96421337-15.20293521zm.9790682.9656915h12.9642134l-12.9642134 15.196229zm13.5449021 33.0346972h-13.5043889v-16.0277966h13.5043889z" fill="#000"></path></svg>`;
    // menu.appendChild(authorContainer);

    const a11yToggles = document.querySelectorAll('.a11y-toggle');
    a11yToggles.forEach((toggle) => {
      toggle.addEventListener("keydown", (event) => {
        const e = event as KeyboardEvent; 
        if (e.key === "Enter" || e.keyCode === 13) {
          toggle.querySelector('input')?.click();
        }
      });
    });

    style.innerHTML += styleContents;
    let head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(style);

    let menuOpen = false;
    const menuToggle = <HTMLButtonElement> document.querySelector('#a11y-menu-toggle');
    const menuToggleText = <HTMLSpanElement> document.querySelector('.a11y-menu-toggle-text');
    const menuToggleIcon = <HTMLSpanElement> document.querySelector('.a11y-menu-toggle-icon');

    // < --------------- SCROLL HIDE/SHOW LOGIC --------------- > //
    let lastScrollY = window.scrollY;
    let ticking = false;
    let hideClass = 'a11y-menu-toggle--hidden-right';

    if (["top-left", "bottom-left"].includes(config.widgetPos.desktop)) {
      hideClass = 'a11y-menu-toggle--hidden-left';
    } else if (["top-right", "bottom-right"].includes(config.widgetPos.desktop)) {
      hideClass = 'a11y-menu-toggle--hidden-right';
    }
    let scrollTimeout: number | undefined;
    function onScroll() {
      if (!menuToggle) return;
      const currentScrollY = window.scrollY;
      if (window.innerWidth <= 1024) {
        if (currentScrollY !== lastScrollY && currentScrollY > 50) {
          menuToggle.classList.add(hideClass);
        }
        if (scrollTimeout) window.clearTimeout(scrollTimeout);
        scrollTimeout = window.setTimeout(() => {
          menuToggle.classList.remove(hideClass);
        }, 400);
      } else {
        menuToggle.classList.remove(hideClass);
      }
      lastScrollY = currentScrollY;
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    });

    if (menuToggle) {
      const checkIfClickIsOutside = (event: MouseEvent) => {
        if (!menu.contains(event.target as Node) && !menuToggle.contains(event.target as Node)) {
          closeMenu();
        }
      }

      const openMenu = () => {
        menuDrawer.classList.add('open');
        menuToggle.classList.add('open');
        menuToggle.ariaExpanded = 'true';
        menuToggle.setAttribute('data-translation-aria-field', 'true');
        menuToggle.setAttribute('data-translation-key', 'closeA11ySettingsAria');
        menu.inert = false;
        window.addEventListener("keydown", closeViaEscape);
        window.addEventListener("click", checkIfClickIsOutside);
        menuToggle.ariaLabel = getTranslation('closeA11ySettingsAria');
        menuOpen = true;
        menuToggleText.textContent = getTranslation('close');
        menuToggleIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path fill="currentColor" d="m12 13.4l2.9 2.9q.275.275.7.275t.7-.275t.275-.7t-.275-.7L13.4 12l2.9-2.9q.275-.275.275-.7t-.275-.7t-.7-.275t-.7.275L12 10.6L9.1 7.7q-.275-.275-.7-.275t-.7.275t-.275.7t.275.7l2.9 2.9l-2.9 2.9q-.275.275-.275.7t.275.7t.7.275t.7-.275zm0 8.6q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-8"/>
        </svg>`;
      }

      const closeMenu = () => {
        menuDrawer.classList.remove('open');
        menuToggle.classList.remove('open');
        menuToggle.ariaExpanded = 'false';
        menuToggle.setAttribute('data-translation-aria-field', 'true');
        menuToggle.setAttribute('data-translation-key', 'openA11ySettingsAria');
        menu.inert = true;
        window.removeEventListener("keydown", closeViaEscape);
        window.removeEventListener("click", checkIfClickIsOutside);
        menuToggle.ariaLabel = getTranslation('openA11ySettingsAria');
        menuOpen = false;
        menuToggleText.textContent = getTranslation('a11y');
        menuToggleIcon.innerHTML = `<svg class="a11y-icon" aria-hidden="true" focusable="false" viewBox="0 0 51 51" xmlns="http://www.w3.org/2000/svg">
          <path d="M25.5 0C11.417 0 0 11.417 0 25.5S11.417 51 25.5 51 51 39.583 51 25.5 39.583 0 25.5 0zm-.385 5.064a3.3 3.3 0 0 1 3.307 3.291 3.304 3.304 0 0 1-3.307 3.306 3.3 3.3 0 0 1-3.29-3.306 3.29 3.29 0 0 1 3.29-3.291zm14.289 10.652l-9.809 1.24.005 9.817 4.755 15.867a1.85 1.85 0 0 1-1.344 2.252c-.989.25-2.003-.3-2.252-1.298l-4.87-14.443h-1.498l-4.48 14.742c-.374.964-1.448 1.404-2.407 1.03-.954-.37-1.533-1.454-1.158-2.418l4.115-15.572v-9.978l-9.04-1.228c-.928-.075-1.558-.89-1.483-1.818.07-.934.914-1.628 1.838-1.554l10.982.944h4.815l11.69-.963a1.68 1.68 0 0 1 1.749 1.623c.04.924-.68 1.718-1.608 1.758z" fill="currentColor"></path>
        </svg>`;
      }

      const toggleMenu = () => {
        menuOpen = !menuOpen;
        if (menuOpen) {
          openMenu();
        } else {
          closeMenu();
        }
      }

      const closeViaEscape = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (event.key == "Escape") {
          toggleMenu();
          menuToggle.focus();
        }
      }

      menuToggle?.addEventListener("click", () => {
        toggleMenu();
      });

      const closeMenuButton = <HTMLButtonElement> document.querySelector(".a11y-close-menu");
      closeMenuButton.addEventListener("click", () => {
        closeMenu();
      });
    }

    // < --------------- DEBUGGING --------------- > //
    // const wrapper = document.querySelector('.hero-fullscreen-slider__slider');
    // console.log(wrapper);
    // const observer = new MutationObserver(() => {
    //   console.log('Swiper updated transition:', wrapper.style.transitionDuration);
    // });
    // observer.observe(wrapper, { attributes: true, attributeFilter: ['style'] });

    // < --------------- DEV MODE --------------- > //
    setupDevTools(modulesContainer);

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        menuDrawer.style.setProperty('--drawer_height', menu.offsetHeight + 'px');
      }
    });
    resizeObserver.observe(menu);

    window.addEventListener('a11y-update', () => {
      requestAnimationFrame(() => {
        menuDrawer.style.setProperty('--drawer_height', menu.offsetHeight + 'px');
      });
    });
  });
}
